import { useIntl } from "react-intl";
import AppRowContainer from "@lib/components/AppComponents/AppRowContainer";
import { Col } from "antd";
import { useMemo } from "react";
import { ApiParams, CurrencyEnum, TransactionProxyStatus } from "@lib/types";
import { useQuery } from "@tanstack/react-query";
import Revenue from "@lib/components/MiniComponents/Dashboard/Revenue";
import CustomPieChart from "@lib/components/MiniComponents/Dashboard/PieChart";
import { DateRangeFilter } from "@lib/components/MiniComponents/Others";
import { transactionStatusColor } from "@lib/helpers/ColorHelper";
import MonthlyPaymentProviderChart from "@lib/components/MiniComponents/Dashboard/MonthlyPaymentProviderChart";
import { apiGetTransactions } from "@lib/api/transaction";

type Props = {
  dateBetween: [string, string];
  setDateBetween: (date: [string, string]) => void;
};

const ManagementPaymentProvider: React.FC<Props> = ({ dateBetween }) => {
  const { messages } = useIntl();

  const params = useMemo(() => {
    return {
      s: [
        {
          $and: [
            { createdAt: { $gte: dateBetween[0] } },
            { createdAt: { $lte: dateBetween[1] } },
          ],
        },
      ],
      join: ["customer"],
    } as ApiParams;
  }, [dateBetween]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryFn: () => apiGetTransactions(params),
    queryKey: ["payment-provider-transactions", JSON.stringify(params)],
    enabled: !!params,
  });
  const apiData = data?.data?.data;
  const currency = CurrencyEnum.USD;
  const paymentHistoryTotalAmount = useMemo(() => {
    let amount = 0;
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        amount += Number(apiData[index].convertedAmount || 0);
      }
    }
    return amount;
  }, [apiData]);

  const paymentHistoryTotalPaid = useMemo(() => {
    let amount = 0;
    if (apiData) {
      const paid = apiData.filter(
        (d) => d.status === TransactionProxyStatus.Success,
      );
      for (let index = 0; index < paid.length; index++) {
        amount += Number(paid[index].convertedAmount || 0);
      }
    }
    return amount;
  }, [apiData]);

  const invoiceStatusGroup = useMemo(() => {
    const data: {
      name: string;
      value: number;
      color: string;
    }[] = [];
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        const item = apiData[index];
        const name =
          (messages["enums.transactionStatus"][item.status] as string) ||
          item.status;
        const findIndex = data.findIndex((d) => d.name == name);
        if (findIndex == -1) {
          data.push({
            name: name,
            value: 1,
            color: transactionStatusColor(item.status),
          });
        } else {
          data[findIndex].value++;
        }
      }
    }
    return data.sort((a, b) => b.value - a.value);
  }, [apiData]);

  return (
    <AppRowContainer>
      <Col xs={24} md={12} lg={8}>
        <CustomPieChart
          title={messages["common.paymentHistory"] as string}
          graph="graph2"
          data={invoiceStatusGroup}
          refetch={refetch}
          loading={isLoading || isFetching}
          extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
          cardProps={{
            borderFull: true,
          }}
        />
      </Col>

      <Col xs={24} md={12} lg={8}>
        <Revenue
          title={messages["common.revenue"] as string}
          data={{
            currency: currency,
            paid: paymentHistoryTotalPaid,
            total: paymentHistoryTotalAmount,
          }}
          refetch={refetch}
          loading={isLoading || isFetching}
          extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
          cardProps={{
            borderFull: true,
          }}
        />
      </Col>

      <Col xs={24} md={12} lg={8}>
        <MonthlyPaymentProviderChart />
      </Col>
    </AppRowContainer>
  );
};

export default ManagementPaymentProvider;
